import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'Vom Upload zum Ergebnis — ein 15-Minuten-Walkthrough',
    summary: 'Ein vollständiger End-to-End-Durchlauf: Gemeinschaft erstellen, eine Datei hochladen, eine Prognose und ein erstes Optimierungsergebnis abrufen.',
    body: (
      <>
        <p>
          Dieser Artikel verbindet alles miteinander. Führen Sie ihn einmal durch und Sie haben
          jedes Werkzeug mindestens einmal verwendet. Es wird vorausgesetzt, dass Sie eine stündliche
          Verbrauchsdatei für ein einzelnes Gebäude bereit haben.
        </p>

        <ol>
          <li>
            <strong>Gemeinschaft erstellen.</strong> Übersicht → <em>Communities</em> →
            {' '}<em>New community</em>. Vergeben Sie einen Namen und wählen Sie Land und Währung. Speichern.
          </li>
          <li>
            <strong>Mindestens ein Gebäude hinzufügen.</strong> Öffnen Sie die Gemeinschaft → <em>Gebäude</em> →
            {' '}<em>Add building</em>. Gebäudename, -typ und eine Postleitzahl reichen für den Anfang.
          </li>
          <li>
            <strong>Lastprofil hochladen.</strong> Gemeinschaft → <em>Datenverwaltung</em> → wählen Sie
            {' '}<em>Building Load Profile</em>, wählen Sie das Gebäude und legen Sie die CSV-Datei ab. Siehe
            {' '}<ArticleLink to="data-upload-how">Eine Datei hochladen, Schritt für Schritt</ArticleLink>, wenn
            die Seite neu für Sie ist.
          </li>
          <li>
            <strong>(Empfohlen) Eine Tarifdatei hochladen.</strong> Ohne Preise sind Kostenergebnisse
            nur illustrativ. Siehe <ArticleLink to="data-tariff">Tarifdateien</ArticleLink>.
          </li>
          <li>
            <strong>Eine Prognose ausführen.</strong> Community-Seitenleiste → <em>Prognose</em>. Die Seite hat
            zwei Tabs — <em>Kurzfristig (Day-Ahead)</em> und <em>Langfristig (monatlich)</em>. Wählen Sie ein
            Gebäude und klicken Sie auf <em>Prognose ausführen</em>. Siehe
            {' '}<ArticleLink to="analysis-forecast">Eine Prognose ausführen</ArticleLink>.
          </li>
          <li>
            <strong>Eine Optimierung ausführen.</strong> Community-Seitenleiste → <em>Optimierung</em> (die
            Seite selbst ist mit „Optimierung" betitelt). Wählen Sie ein Ziel (z. B. <em>Kosten minimieren</em>)
            und klicken Sie auf <em>Day-Ahead-Optimierung ausführen</em>. Siehe
            {' '}<ArticleLink to="analysis-optimisation">Eine Optimierung ausführen</ArticleLink>.
          </li>
          <li>
            <strong>Ergebnisse lesen.</strong> Zahlen, Diagramme und Einsparungen erscheinen auf derselben Seite.
            Was jeder KPI bedeutet, finden Sie in <ArticleLink to="results-glossary">dem Glossar</ArticleLink>;
            wie jedes Diagramm zu interpretieren ist, steht im Rest dieses Abschnitts.
          </li>
        </ol>

        <Tip>
          Die Plattform zeigt klar beschriftete illustrative Ergebnisse an, wenn ein Dienst nicht verfügbar ist oder
          erforderliche Daten nicht hochgeladen wurden. Wenn ein Ergebnis verdächtig rund wirkt (z. B. genau
          100 €/Jahr), prüfen Sie die Seite auf einen Hinweis „Beispieldaten".
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Eine Prognose ausführen',
    summary: 'Vorhersage der nächsten Stunden/Tage für Verbrauch oder Erzeugung eines Gebäudes oder einer Gemeinschaft.',
    body: (
      <>
        <p>
          Eine <strong>Prognose</strong> nimmt Ihre vergangenen Daten und sagt künftige Last oder Erzeugung voraus.
          Nutzen Sie sie zur Betriebsplanung, um eine Day-Ahead-Optimierung anzutreiben, oder einfach um zu sehen,
          wie morgen — oder das nächste Quartal — aussieht.
        </p>

        <h5 className="mt-3">Voraussetzungen</h5>
        <ul>
          <li>Die Gemeinschaft hat mindestens ein Gebäude mit einem hochgeladenen <strong>Building Load Profile</strong>
            (mehr Verlauf → bessere Prognose — für zuverlässige Prognosen werden mindestens 3 Monate Daten empfohlen,
            idealerweise ein Jahr).</li>
          <li>Für <em>Solar-</em> oder <em>Windprognosen</em> benötigen Sie außerdem eine Erzeugungsdatei
            (Gemeinschafts-PV-/Windprofil oder die Dach-PV des Gebäudes). Eine
            {' '}<ArticleLink to="data-weather">Wetterdatei</ArticleLink> ist optional, verbessert aber die
            Genauigkeit um 15–30%.</li>
        </ul>

        <h5 className="mt-3">Schritt für Schritt</h5>
        <ol>
          <li>Öffnen Sie die Gemeinschaft → wählen Sie <strong>Prognose</strong> in der Seitenleiste.</li>
          <li>Wählen Sie den <strong>Modus</strong> aus den zwei Tabs oben auf der Seite:
            <ul>
              <li><strong>Kurzfristig (Day-Ahead)</strong> — sagt die nächsten 24 Stunden ab einem gewählten
                Zieldatum voraus (bis zu 7 Tage im Voraus).</li>
              <li><strong>Langfristig (monatlich)</strong> — projiziert monatliche Werte über einen von Ihnen
                gewählten Horizont: <em>3 Monate</em>, <em>6 Monate</em> oder <em>12 Monate</em>.</li>
            </ul>
          </li>
          <li>Wählen Sie die <strong>Anlage(n)</strong> für die Prognose: die Last eines Gebäudes, die
            Gemeinschafts-PV-Erzeugung oder die Gemeinschafts-Windenergie-Erzeugung. Sie können mehr als eine auswählen.</li>
          <li>Klicken Sie auf <strong>Prognose ausführen</strong> (die Beschriftung der Schaltfläche passt sich
            an die Anzahl der ausgewählten Anlagen an). Ein Ladekreis zeigt <em>Prognose wird ausgeführt…</em>
            während der Vorgang läuft.</li>
          <li>Die Ergebnisse erscheinen im rechten Panel. Siehe
            {' '}<ArticleLink to="results-forecast">Das Prognosediagramm lesen</ArticleLink>
            {' '}für die Bedeutung jeder Linie und jedes Bandes.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="Die Prognoseseite: Tab wählen, Anlagen wählen, ausführen."
        />

        <Tip>
          Wenn dieselbe Prognose von Durchlauf zu Durchlauf sehr unterschiedlich aussieht, haben Sie wahrscheinlich
          sehr wenig Eingabedaten. Fügen Sie mehr Verlaufsdaten hinzu und führen Sie die Prognose erneut aus. Siehe
          {' '}<ArticleLink to="tb-forecast-flat">„Meine Prognose sieht flach oder falsch aus"</ArticleLink>
          {' '}in der Fehlerbehebung, wenn die Ergebnisse weiterhin seltsam erscheinen.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Eine Optimierung ausführen (Optimierung)',
    summary: 'Entscheiden Sie, wann importiert, exportiert, geladen oder entladen wird — um Kosten zu minimieren oder Autarkie zu maximieren.',
    body: (
      <>
        <p>
          Das <strong>Optimierung</strong>-Werkzeug findet den besten Fahrplan für die flexiblen Anlagen Ihrer
          Gemeinschaft — wann eine Batterie geladen oder entladen wird, wann Elektrofahrzeuge geladen werden,
          wann aus dem Netz importiert und wann exportiert wird — nach einem von Ihnen gewählten Ziel.
        </p>

        <h5 className="mt-3">Voraussetzungen</h5>
        <ul>
          <li>Eine Gemeinschaft mit mindestens einer hochgeladenen <strong>Last</strong>datei.</li>
          <li>Eine <strong>Tarif</strong>datei (der Optimierer benötigt Preise, um zu wissen, was günstig und
            was teuer ist). Siehe <ArticleLink to="data-tariff">Tarifdateien</ArticleLink>.</li>
          <li>Optional, aber empfohlen: eine <strong>Erzeugungs</strong>datei und alle für die Gemeinschaft
            eingetragenen{' '}<strong>Anlagen</strong> (PV, Batterie, EV).</li>
        </ul>

        <h5 className="mt-3">Schritt für Schritt</h5>
        <ol>
          <li>Öffnen Sie die Gemeinschaft → wählen Sie <strong>Optimierung</strong> in der Seitenleiste.
            (Die Seite selbst ist mit „Optimierung" betitelt — gleicher Ort.)</li>
          <li>Wählen Sie ein <strong>Ziel</strong>:
            <ul>
              <li><em>Kosten minimieren</em> — kaufen Sie bei niedrigen Preisen, verkaufen Sie bei hohen Preisen.
                Standard für die meisten Gemeinschaften.</li>
              <li><em>Autarkie maximieren</em> — nutzen Sie so viel wie möglich von Ihrer eigenen Erzeugung,
                auch wenn die Kosten leicht höher sind.</li>
              <li><em>Eigenverbrauch maximieren</em> — minimieren Sie den Export Ihrer Erzeugung.</li>
            </ul>
          </li>
          <li>Die Optimierung erstellt einen Tagesfahrplan (24 Stunden) für den nächsten Tag.</li>
          <li>Prüfen Sie die Anlagenliste (PV, Batterie, EV-Ladegeräte). Batterieeinstellungen wie
            {' '}<em>minimaler/maximaler Ladezustand</em> und <em>Hin- und Rückwirkungseffizienz</em> sollten der
            Realität entsprechen — ändern Sie diese andernfalls.</li>
          <li>Klicken Sie auf <strong>Day-Ahead-Optimierung ausführen</strong>. Ein Ladekreis zeigt
            <em>Optimierung wird ausgeführt…</em>
            (möglicherweise sehen Sie auch eine Meldung „Solving the MILP problem" — das ist der mathematische
            Löser bei der Arbeit). Ein Durchlauf mit wenigen Gebäuden ist in der Regel in deutlich unter einer
            Minute abgeschlossen.</li>
          <li>Nach Abschluss erscheint das Ergebnispanel darunter — KPI-Karten oben, stündliche Energiebilanz
            und Batteriediagramme darunter. Siehe
            {' '}<ArticleLink to="results-optimisation-kpis">Die Optimierungs-KPIs lesen</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="Die Optimierungsseite: Ziel wählen, Anlagen prüfen, ausführen."
        />

        <Warn>
          Wenn der Durchlauf mit „infeasible" endet, können nicht alle Bedingungen gleichzeitig erfüllt werden
          (beispielsweise ist die Batterie für den Bedarf zu klein). Siehe
          {' '}<ArticleLink to="tb-optimisation-infeasible">„Optimierung meldet infeasible"</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
